import type { Action } from '@ngrx/store';
import type { ProjectState } from 'app/store/project/project.state';
import type { ProjectOutput } from 'core/http/dev';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { allAssignedProjects } from 'app/store/login/assigned-projects/assigned-projects.selectors';
import { getCourierChains } from 'app/store/project/courier-chains/courier-chains.actions';
import { courierChainsState } from 'app/store/project/courier-chains/courier-chains.selectors';
import { getCouriers } from 'app/store/project/couriers/couriers.actions';
import { couriersState } from 'app/store/project/couriers/couriers.selectors';
import { markCurrentProjectLoaded, saveCurrentProject } from 'app/store/project/current-project/current-project.actions';
import { currentProjectState } from 'app/store/project/current-project/current-project.selectors';
import { getDocuments } from 'app/store/project/documents/documents.actions';
import { documentsState } from 'app/store/project/documents/documents.selectors';
import { getExtensions } from 'app/store/project/extensions/extensions.actions';
import { extensionsState } from 'app/store/project/extensions/extensions.selectors';
import { getOrderFieldMap } from 'app/store/project/field-map/field-map.actions';
import { orderFieldMapState } from 'app/store/project/field-map/field-map.selectors';
import { getFilterTemplates } from 'app/store/project/filter-templates/filter-templates.actions';
import { filterTemplatesState } from 'app/store/project/filter-templates/filter-templates.selectors';
import { getGoods } from 'app/store/project/goods/goods.actions';
import { goodsState } from 'app/store/project/goods/goods.selectors';
import { getNotices } from 'app/store/project/notices/notices.actions';
import { noticesState } from 'app/store/project/notices/notices.selectors';
import { resetAllPageCriteria } from 'app/store/project/page-criteria/page-criteria.actions';
import { getPools } from 'app/store/project/pools/pools.actions';
import { poolsState } from 'app/store/project/pools/pools.selectors';
import { getProjectResources } from 'app/store/project/project-resources/project-resources.actions';
import { projectResourcesState } from 'app/store/project/project-resources/project-resources.selectors';
import { getScheduledTasks } from 'app/store/project/scheduled-tasks/scheduled-tasks.actions';
import { scheduledTasksState } from 'app/store/project/scheduled-tasks/scheduled-tasks.selectors';
import { getSenders } from 'app/store/project/senders/senders.actions';
import { sendersState } from 'app/store/project/senders/senders.selectors';
import { getStatusChains } from 'app/store/project/status-chains/status-chains.actions';
import { statusChainsState } from 'app/store/project/status-chains/status-chains.selectors';
import { getStatuses } from 'app/store/project/statuses/statuses.actions';
import { statusesState } from 'app/store/project/statuses/statuses.selectors';
import { getStorehouses } from 'app/store/project/storehouses/storehouses.actions';
import { storehousesState } from 'app/store/project/storehouses/storehouses.selectors';
import { MonthsDebtsInput, PrettyDebt, ProjectService } from 'core/http/lg-logistic-rest-api/Auth/project.service';
import { ownedError } from 'core/utils/owned-error';
import { selectFirst } from 'core/utils/rx-common';
import moment from 'moment';
import { defer, forkJoin, Observable } from 'rxjs';
import { first, map, skip, switchMap } from 'rxjs/operators';

@Injectable()
export class ProjectByRoute {
  constructor(private store$: Store) { }

  resolve(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<ProjectOutput> {
    const projectRoute = route.params.route;

    return this.store$.pipe(
      selectFirst(allAssignedProjects),
      map((projects) => {
        const project = projects.find(p => p.route === projectRoute);

        if (!project) {
          throw ownedError(new Error(`Project route '${projectRoute}' is not consider to existing projects`));
        }

        this.store$.dispatch(saveCurrentProject(project));
        this.store$.dispatch(markCurrentProjectLoaded());

        return project;
      }),
    );
  }
}

@Injectable()
export class MonthsDebtsResolver {
  constructor(private projectService: ProjectService) {}

  resolve(): Observable<PrettyDebt[]> {
    const today = moment().format('YYYY-MM-DD');
    const sixMonthsAgo = moment()
      .subtract(5, 'months')
      .set('date', 1)
      .format('YYYY-MM-DD');

    const input: MonthsDebtsInput = {
      cutDate: today,
      unloadedFrom: sixMonthsAgo,
      unloadedTo: today,
    };

    return this.projectService.getMonthsDebts(input);
  }
}

@Injectable()
export class ProjectStateResolver {
  constructor(private store$: Store<ProjectState>) { }

  resolve(): Observable<any> {
    return defer(() => {
      this.store$.dispatch(resetAllPageCriteria());
      return forkJoin({
        resources: this.loadResource(this.store$.select(projectResourcesState), getProjectResources()),
        currentProject: this.store$.select(currentProjectState).pipe(first(s => s.isLoaded)),
      });
    }).pipe(
      switchMap(() => {
        return forkJoin({
          couriers: this.loadResource(this.store$.select(couriersState), getCouriers()),
          courierChains: this.loadResource(this.store$.select(courierChainsState), getCourierChains()),
          documents: this.loadResource(this.store$.select(documentsState), getDocuments()),
          pools: this.loadResource(this.store$.select(poolsState), getPools()),
          goods: this.loadResource(this.store$.select(goodsState), getGoods()),
          scheduledTasks: this.loadResource(this.store$.select(scheduledTasksState), getScheduledTasks()),
          senders: this.loadResource(this.store$.select(sendersState), getSenders()),
          statuses: this.loadResource(this.store$.select(statusesState), getStatuses()),
          statusChains: this.loadResource(this.store$.select(statusChainsState), getStatusChains()),
          storehouses: this.loadResource(this.store$.select(storehousesState), getStorehouses()),
          orderFieldMap: this.loadResource(this.store$.select(orderFieldMapState), getOrderFieldMap()),
          filterTemplates: this.loadResource(this.store$.select(filterTemplatesState), getFilterTemplates()),
          notices: this.loadResource(this.store$.select(noticesState), getNotices()),
          extensions: this.loadResource(this.store$.select(extensionsState), getExtensions()),
        });
      }),
    );
  }

  private loadResource<T extends { loading: boolean; error: Error | null; loaded?: boolean; isLoaded?: boolean }>(
    source: Observable<T>,
    action: Action,
  ): Observable<T> {
    return new Observable((subscriber) => {
      let started = false;
      const subscription = source.pipe(
        skip(1),
        first((state) => {
          started ||= state.loading;
          return started && !state.loading && Boolean(state.loaded || state.isLoaded || state.error);
        }),
        map((state) => {
          if (state.error) {
            throw state.error;
          }
          return state;
        }),
      ).subscribe(subscriber);
      this.store$.dispatch(action);
      return subscription;
    });
  }
}
